import { useState, useEffect } from "react";
import { X, ShoppingBag } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { resolveProductOptions } from "@/lib/productOptions";

const labelStyle = {
  fontFamily: "Unbounded, sans-serif",
  fontSize: "0.55rem",
  color: "#A1A1AA",
  letterSpacing: "0.18em",
  textTransform: "uppercase",
  marginBottom: "0.7rem",
  display: "block",
};

const chipBase = {
  padding: "0.5rem 1rem",
  background: "transparent",
  border: "1px solid rgba(255,255,255,0.15)",
  color: "#A1A1AA",
  fontFamily: "Unbounded, sans-serif",
  fontSize: "0.7rem",
  fontWeight: 700,
  letterSpacing: "0.05em",
  cursor: "pointer",
  borderRadius: "999px",
  transition: "all 0.15s ease",
};

const chipActive = {
  background: "#FF005A",
  border: "1px solid #FF005A",
  color: "#FFFFFF",
  boxShadow: "0 0 18px rgba(255,0,90,0.45)",
};

/**
 * Modal triggered when user clicks "Add to cart" on a product with options.
 * Size / color / custom variant lists come from resolveProductOptions — same
 * source as the PDP preview (no invented catalogs, no size-like EXTRA).
 */
export default function AddToCartModal({ product, open, onClose, onConfirm }) {
  const { lang } = useLanguage();
  const opts = resolveProductOptions(product);
  const {
    sizeType,
    adultSizes,
    childSizes,
    legacySizes,
    colors,
    customVariants,
    showSizes,
    showColors,
    showCustom,
    hasAdultChildToggle,
  } = opts;

  const [mode, setMode] = useState(() => (sizeType === "child" ? "child" : "adult"));
  const [size, setSize] = useState("");
  const [color, setColor] = useState("");
  const [customSel, setCustomSel] = useState({});
  const [qty, setQty] = useState(1);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSize("");
    setColor("");
    setCustomSel({});
    setQty(1);
    setTouched(false);
    setMode(sizeType === "child" ? "child" : "adult");
  }, [open, sizeType, product?._id]);

  if (!open || !product) return null;

  const hasLegacy = sizeType === "legacy";
  const sizes = hasLegacy
    ? legacySizes
    : (mode === "child" ? childSizes : adultSizes);
  const customComplete = customVariants.every((cv) => cv.required === false || !!customSel[cv.label]);
  const canConfirm = (!showSizes || size) && (!showColors || color) && (!showCustom || customComplete);

  const handleConfirm = () => {
    setTouched(true);
    if (!canConfirm) return;
    onConfirm({
      size: showSizes ? size : null,
      color: showColors ? color : null,
      custom_options: showCustom && Object.keys(customSel).length > 0 ? customSel : null,
      quantity: qty,
    });
    onClose();
  };

  return (
    <div
      data-testid="add-to-cart-modal"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)",
        backdropFilter: "blur(8px)", zIndex: 90,
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "1rem",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#0A0A0A",
          border: "1px solid rgba(255,255,255,0.08)",
          borderRadius: "16px",
          maxWidth: "440px",
          width: "100%",
          padding: "1.8rem",
          position: "relative",
        }}
      >
        <button
          onClick={onClose}
          data-testid="cart-modal-close"
          style={{ position: "absolute", top: "12px", right: "12px", background: "transparent", border: "none", cursor: "pointer", color: "#A1A1AA" }}
        >
          <X size={20} />
        </button>

        <p style={{ fontFamily: "Unbounded, sans-serif", fontSize: "0.55rem", color: "#FF005A", letterSpacing: "0.2em", marginBottom: "0.4rem" }}>
          {lang === "cz" ? "DO KOŠÍKU" : "ADD TO CART"}
        </p>
        <h3 style={{ fontFamily: "Unbounded, sans-serif", fontWeight: 800, fontSize: "1.2rem", color: "#FFFFFF", marginBottom: "1.4rem", letterSpacing: "-0.02em" }}>
          {product.name}
        </h3>

        {showSizes && (
          <div style={{ marginBottom: "1.3rem" }}>
            <span style={labelStyle}>
              {lang === "cz" ? "VELIKOST" : "SIZE"}
              <span style={{ color: "#FF005A", marginLeft: "0.3rem" }}>*</span>
            </span>

            {hasAdultChildToggle && (
              <div className="flex gap-2 mb-3">
                <button
                  type="button"
                  data-testid="size-mode-adult"
                  onClick={() => { setMode("adult"); setSize(""); }}
                  style={{
                    padding: "0.4rem 0.9rem",
                    background: mode === "adult" ? "#FF005A" : "transparent",
                    border: `1px solid ${mode === "adult" ? "#FF005A" : "rgba(255,255,255,0.15)"}`,
                    color: mode === "adult" ? "#FFF" : "#A1A1AA",
                    fontFamily: "Unbounded, sans-serif",
                    fontSize: "0.55rem",
                    letterSpacing: "0.1em",
                    fontWeight: 700,
                    cursor: "pointer",
                    borderRadius: "999px",
                  }}
                >
                  {lang === "cz" ? "DOSPĚLÝ" : "ADULT"}
                </button>
                <button
                  type="button"
                  data-testid="size-mode-child"
                  onClick={() => { setMode("child"); setSize(""); }}
                  style={{
                    padding: "0.4rem 0.9rem",
                    background: mode === "child" ? "#FF005A" : "transparent",
                    border: `1px solid ${mode === "child" ? "#FF005A" : "rgba(255,255,255,0.15)"}`,
                    color: mode === "child" ? "#FFF" : "#A1A1AA",
                    fontFamily: "Unbounded, sans-serif",
                    fontSize: "0.55rem",
                    letterSpacing: "0.1em",
                    fontWeight: 700,
                    cursor: "pointer",
                    borderRadius: "999px",
                  }}
                >
                  {lang === "cz" ? "DĚTSKÝ VĚK" : "CHILD AGE"}
                </button>
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              {sizes.map((s) => (
                <button
                  type="button"
                  key={s}
                  data-testid={`size-chip-${s}`}
                  onClick={() => setSize(s)}
                  style={{ ...chipBase, ...(size === s ? chipActive : {}) }}
                >
                  {hasLegacy || mode !== "child" ? s : `${s} ${lang === "cz" ? "let" : "y"}`}
                </button>
              ))}
            </div>
            {touched && !size && (
              <p style={{ color: "#FF005A", fontSize: "0.7rem", marginTop: "0.4rem" }}>
                {lang === "cz" ? "Vyber prosím velikost" : "Please choose a size"}
              </p>
            )}
          </div>
        )}

        {showColors && (
          <div style={{ marginBottom: "1.3rem" }}>
            <span style={labelStyle}>
              {lang === "cz" ? "BARVA" : "COLOR"}
              <span style={{ color: "#FF005A", marginLeft: "0.3rem" }}>*</span>
            </span>
            <div className="flex flex-wrap gap-3">
              {colors.map((c) => (
                <button
                  type="button"
                  key={c.name}
                  data-testid={`color-chip-${c.name}`}
                  onClick={() => setColor(c.name)}
                  title={c.name}
                  style={{
                    width: "44px", height: "44px", borderRadius: "50%",
                    background: c.hex || "#888",
                    border: color === c.name ? "3px solid #FF005A" : "3px solid rgba(255,255,255,0.15)",
                    cursor: "pointer",
                    boxShadow: color === c.name ? "0 0 18px rgba(255,0,90,0.5)" : "none",
                    transition: "all 0.15s ease",
                  }}
                />
              ))}
            </div>
            {color && (
              <p style={{ color: "#A1A1AA", fontSize: "0.7rem", marginTop: "0.4rem", fontFamily: "Manrope, sans-serif" }}>
                {color}
              </p>
            )}
            {touched && !color && (
              <p style={{ color: "#FF005A", fontSize: "0.7rem", marginTop: "0.4rem" }}>
                {lang === "cz" ? "Vyber prosím barvu" : "Please choose a color"}
              </p>
            )}
          </div>
        )}

        {showCustom && customVariants.map((cv, idx) => (
          <div key={cv.label} style={{ marginBottom: "1.3rem" }} data-testid={`custom-variant-block-${idx}`}>
            <span style={labelStyle}>
              {cv.label}
              {cv.required !== false && <span style={{ color: "#FF005A", marginLeft: "0.3rem" }}>*</span>}
            </span>
            <div className="flex flex-wrap gap-2">
              {(cv.options || []).map((opt) => (
                <button
                  type="button"
                  key={opt}
                  data-testid={`custom-chip-${cv.label}-${opt}`}
                  onClick={() => setCustomSel((s) => ({ ...s, [cv.label]: opt }))}
                  style={{ ...chipBase, ...(customSel[cv.label] === opt ? chipActive : {}) }}
                >
                  {opt}
                </button>
              ))}
            </div>
            {touched && cv.required !== false && !customSel[cv.label] && (
              <p style={{ color: "#FF005A", fontSize: "0.7rem", marginTop: "0.4rem" }}>
                {lang === "cz" ? `Vyber prosím ${cv.label.toLowerCase()}` : `Please choose ${cv.label.toLowerCase()}`}
              </p>
            )}
          </div>
        ))}

        <div style={{ marginBottom: "1.6rem" }}>
          <span style={labelStyle}>{lang === "cz" ? "POČET KUSŮ" : "QUANTITY"}</span>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setQty((q) => Math.max(1, q - 1))}
              data-testid="qty-minus"
              style={{ width: 36, height: 36, borderRadius: 8, background: "#050505", border: "1px solid rgba(255,255,255,0.15)", color: "#FFFFFF", cursor: "pointer" }}
            >−</button>
            <span data-testid="qty-value" style={{ fontFamily: "Unbounded, sans-serif", fontWeight: 800, color: "#FFFFFF", fontSize: "1rem", minWidth: 40, textAlign: "center" }}>
              {qty}
            </span>
            <button
              type="button"
              onClick={() => setQty((q) => q + 1)}
              data-testid="qty-plus"
              style={{ width: 36, height: 36, borderRadius: 8, background: "#050505", border: "1px solid rgba(255,255,255,0.15)", color: "#FFFFFF", cursor: "pointer" }}
            >+</button>
            <span style={{ marginLeft: "auto", color: "#FF005A", fontFamily: "Unbounded, sans-serif", fontWeight: 800, fontSize: "1.2rem" }}>
              {(product.price * qty).toFixed(0)} Kč
            </span>
          </div>
        </div>

        <button
          type="button"
          data-testid="confirm-add-to-cart"
          onClick={handleConfirm}
          className="btn-rebel rounded-none w-full flex items-center justify-center gap-2"
          style={{ fontSize: "0.75rem", padding: "0.9rem 1.4rem", opacity: canConfirm ? 1 : 0.55 }}
        >
          <ShoppingBag size={14} />
          {lang === "cz" ? "PŘIDAT DO KOŠÍKU" : "ADD TO CART"}
        </button>
      </div>
    </div>
  );
}
